import { useCallback, useEffect, useState } from 'react';
import constants from '../../../../config/constants';
import { getVariant } from '../../../../utils/variant';
import { fetchImages, filterForSoftline, guessFirstImage, isVideo } from '../../ImageGalleryHelper';

/**
 * Custom hook that encapsulates all business logic for MordenImageGallery
 * Handles image loading, navigation, state management, and digital data integration
 *
 * @param {Object} params - Hook parameters
 * @param {Object} params.product - Product data object
 * @param {Object} params.appCtx - Application context
 * @param {Object} params.labels - Labels for internationalization
 * @param {Object} params.digitalData - Digital data context
 * @param {number} params.desktopImagesPerSlide - Number of images per slide on desktop
 * @param {number} params.imagesPerSlide - Number of images per slide
 * @returns {Object} - Processed data and handlers for component rendering
 */
export const useMordenImageGallery = ({
  product,
  appCtx,
  labels,
  digitalData,
  desktopImagesPerSlide,
  imagesPerSlide,
}) => {
  const { isOutOfStock, attributes, brandName, name, variants, medias, currentVariant, id } = product;
  const { sameImage } = attributes || {};

  const { regionCode, siteConfig } = appCtx || {};
  const { toggles } = siteConfig || {};
  const { isDottedImageCarouselEnabled = false, isCFMediasEnabled = false } = toggles || {};

  const variantId = !sameImage && currentVariant ? currentVariant : id;

  const firstVariant = variants?.[0];
  const firstOffering = firstVariant?.offerings?.[0];
  const sellerName = (firstOffering?.sellerName || '').toLowerCase();

  // State management
  const selectedVariant = useState(variantId);
  const firstImagesGuess = guessFirstImage(product, variantId, sameImage, 'softline');
  const [images, setImages] = useState(firstImagesGuess);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [activeIndicatorIndex, setActiveIndicatorIndex] = useState(0);
  const [carouselImages, setCarouselImages] = useState([]);
  const [isVideoPlaying, setVideoPlaying] = useState();

  // Image loading effect
  useEffect(() => {
    const variantObj = getVariant(variants, currentVariant);

    if (!sameImage && variantObj?.medias && filterForSoftline(variantObj.medias).length > 0) {
      const variantVideosUrls = (variantObj.medias || []).filter(isVideo).map((m) => m.url);
      const productVideos = (medias || []).filter((m) => isVideo(m) && !variantVideosUrls.includes(m.url));
      setImages([...filterForSoftline(variantObj.medias), ...productVideos]);
      return;
    }

    if (sameImage && medias && filterForSoftline(medias).length > 0) {
      setImages(filterForSoftline(medias));
      return;
    }

    const media = variantObj?.medias || medias;
    fetchImages({
      variantId: selectedVariant,
      regionCode,
      sellerName,
      isCFMediasEnabled,
      media,
    })
      .then((img) => {
        if (img) {
          const productVideos = (medias || []).filter(isVideo);
          setImages([...img, ...productVideos]);
        } else {
          setImages(firstImagesGuess);
        }
      })
      .catch(() => {
        setImages(firstImagesGuess);
      });
  }, [currentVariant]);

  // Carousel images effect
  useEffect(() => {
    setCarouselImages(
      images.slice(activeIndicatorIndex * desktopImagesPerSlide, (1 + activeIndicatorIndex) * desktopImagesPerSlide)
    );
  }, [activeIndicatorIndex, images, desktopImagesPerSlide]);

  // Reset active image index when images change
  useEffect(() => {
    setActiveImageIndex(0);
  }, [images]);

  // Navigation handlers
  const onNextHeadline = useCallback(() => {
    let newActiveIndicatorIndex = activeIndicatorIndex;
    let newActiveImageIndex = activeImageIndex + 1;

    const isLastImage = activeImageIndex + 1 === images.length;
    const isLastImageOfSlide = (activeImageIndex + 1) % imagesPerSlide === 0;

    if (!isLastImage && isLastImageOfSlide) {
      newActiveIndicatorIndex = activeIndicatorIndex + 1;
    }

    if (isLastImage) {
      newActiveIndicatorIndex = 0;
      newActiveImageIndex = 0;
    }

    setActiveIndicatorIndex(newActiveIndicatorIndex);
    setActiveImageIndex(newActiveImageIndex);
  }, [activeImageIndex, activeIndicatorIndex, images.length, imagesPerSlide]);

  const onPreviousHeadLine = useCallback(() => {
    let newActiveIndicatorIndex = activeIndicatorIndex;
    let newActiveImageIndex = activeImageIndex - 1;

    const isFirstImage = activeImageIndex === 0;
    const isFirstImageOfSlide = activeImageIndex % imagesPerSlide === 0;

    if (isFirstImage) {
      newActiveImageIndex = images.length - 1;
      newActiveIndicatorIndex = Math.ceil(images.length / imagesPerSlide) - 1;
    } else if (!isFirstImage && isFirstImageOfSlide) {
      newActiveIndicatorIndex = activeIndicatorIndex - 1;
    }

    setActiveIndicatorIndex(newActiveIndicatorIndex);
    setActiveImageIndex(newActiveImageIndex);
  }, [activeImageIndex, activeIndicatorIndex, images.length, imagesPerSlide]);

  const handleMouseOverButton = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Utility functions
  const getImages = useCallback(() => {
    return images;
  }, [images]);

  const getModelsBodyCharacteristics = useCallback(() => {
    const specs = attributes?.specifications || [];
    const modelo = specs.find((spec) => constants.MODEL_CHARACTERISTICS_SPECIFICATION_ID.includes(spec.name));
    return modelo && `${labels.MODEL_CHARACTERISTICS_PREFIX} ${modelo.value}`;
  }, [attributes, labels]);

  // Digital data integration
  const modelCharacteristics = getModelsBodyCharacteristics();

  useEffect(() => {
    digitalData.pdp.setProductFeatures(modelCharacteristics ? '1' : '');
    digitalData.pdp.setProductBigImagesQty(
      // eslint-disable-next-line no-nested-ternary
      images.length > 1 ? 2 : images.length > 0 ? 1 : 0
    );
    digitalData.pdp.setProductSmallImagesQty(images.length);
  }, [digitalData, modelCharacteristics, images.length]);

  return {
    // Processed product data
    isOutOfStock,
    brandName,
    name,
    regionCode,
    isDottedImageCarouselEnabled,

    // State values
    images,
    activeImageIndex,
    activeIndicatorIndex,
    carouselImages,
    isVideoPlaying,

    // State setters
    setActiveImageIndex,
    setActiveIndicatorIndex,
    setVideoPlaying,

    // Event handlers
    onNextHeadline,
    onPreviousHeadLine,
    handleMouseOverButton,

    // Utility functions
    getImages,
    getModelsBodyCharacteristics,

    // Computed values
    modelCharacteristics,
  };
};
